import { 
  calculateSeverityLevel, 
  calculateGroundwaterSeverityLevel, 
  calculateInformalWorkersSeverityLevel,
  calculateLostLaborHoursSeverityLevel,
  calculateNoShadeWaterSeverityLevel,
  calculateHeatIllnessSeverityLevel,
  calculateTransportDisruptionsSeverityLevel,
  calculateSchoolClosuresSeverityLevel,
  calculateSchoolCoolingSeverityLevel,
  calculateWaterSupplySeverityLevel,
  calculateWaterTankersSeverityLevel,
  calculateCoolingSystemFailuresSeverityLevel,
  calculateTrafficSignalFailuresSeverityLevel,
  calculateRoadClosuresSeverityLevel,
  calculateHeatDifferentialSeverityLevel,
  calculateSurfaceTemperatureSeverityLevel,
  calculateShelterOccupancySeverityLevel
} from '../utils/severityUtils';

import API_BASE_URL from './apiConfig';

// Helper function to get city-specific API key
// First checks ConfigurationContext (stored in localStorage), then falls back to default pattern
// Example: "Delhi" -> "DASHBOARD_DELHI_KEY", "New York" -> "DASHBOARD_NEW_YORK_KEY"
const getCityApiKey = (city) => {
  if (!city || typeof city !== 'string') {
    // Default to Delhi for backward compatibility
    return "DASHBOARD_DELHI_KEY";
  }
  
  // Check if configuration exists in localStorage (set by ConfigurationContext)
  try {
    const configStr = localStorage.getItem('dashboard_configurations');
    if (configStr) {
      const config = JSON.parse(configStr);
      if (config.cityApiKeys && config.cityApiKeys[city]) {
        return config.cityApiKeys[city];
      }
    }
  } catch (e) {
    // If parsing fails, fall through to default
  }
  
  // Fallback to default pattern
  const cityKey = city.toUpperCase().replace(/\s+/g, '_');
  return `DASHBOARD_${cityKey}_KEY`;
};

// Helper function to get API headers based on city
// If city is not provided, defaults to Delhi
const getApiHeaders = (city = null) => {
  const apiKey = getCityApiKey(city);
  return { "X-Frontend-API-Key": apiKey };
};

// Default headers for backward compatibility (Delhi)
const API_HEADERS = getApiHeaders('Delhi');

// Log API configuration on module load
console.log('═══════════════════════════════════════════════════════════');
console.log('[API] 📡 API Service Initialized');
console.log('[API] Base URL:', API_BASE_URL);
console.log('[API] Available Endpoints:');
console.log('[API]   • GET /api/weather/daily/{wardNumber}');
console.log('[API]   • GET /api/dashboard/severity/power_outages');
console.log('[API]   • GET /api/forecast/severity/power_outages');
console.log('[API]   • GET /api/dashboard/severity/groundwater');
console.log('[API]   • GET /api/forecast/severity/groundwater');
console.log('[API]   • GET /city_json/{city}_wards.geojson');
console.log('[API] ═══════════════════════════════════════════════════════════');

// Fetch weather data for a specific ward and date
export const fetchWeatherForDate = async (wardNumber, targetDate, city = null) => {
  const endpoint = `/api/weather/daily/${wardNumber}`;
  const startTime = Date.now();
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = { target_date: targetDate };
    const fullUrl = `${url}?${new URLSearchParams(params)}`;
    
    console.log(`[API] 🌐 START: ${endpoint} | Ward: ${wardNumber}, Date: ${targetDate}${city ? `, City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        ...getApiHeaders(city),
        'Content-Type': 'application/json'
      }
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error details:`, errorText);
      return { error: `HTTP ${response.status}: ${response.statusText}`, details: errorText };
    }
    
    const data = await response.json();
    
    if (data.error || (data.message && typeof data.message === 'string' && data.message.trim() !== '')) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} | API returned error message:`, data.error || data.message);
      return { error: data.error || data.message, data: data };
    }
    
    if (!data.weather) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} | No weather object in response`);
      return { error: 'No weather data in response', data: data };
    }
    
    const weather = data.weather;
    
    if (weather.max_temp_c === undefined && weather.min_temp_c === undefined && weather.avg_temp_c === undefined) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} | No temperature data in weather object`);
      return { error: 'No temperature data in weather object', data: data };
    }
    
    const weatherData = {
      max_temp_c: weather.max_temp_c,
      min_temp_c: weather.min_temp_c,
      avg_temp_c: weather.avg_temp_c,
      avg_humidity_percent: weather.avg_humidity_percent,
      max_feels_like_c: weather.max_feels_like_c,
      heat_index: weather.heat_index
    };
    
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Data: avg_temp=${weatherData.avg_temp_c}°C`);
    return weatherData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      return { error: `Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`, exception: true };
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    return { error: error.message, exception: true };
  }
};

// Fetch weather data for a ward over the last 30 days
export const fetchWeatherDataForWard = async (ward, city = null) => {
  if (!ward) return null;
  
  let wardNumber = null;
  if (ward.properties) {
    const wardNoStr = ward.properties.Ward_No || 
                     ward.properties.Ward_Number || 
                     ward.properties.ward_number || 
                     ward.properties.WARD_NUMBER ||
                     ward.properties.WardNumber;
    
    if (wardNoStr) {
      const numericMatch = wardNoStr.toString().match(/^(\d+)$/);
      if (numericMatch) {
        wardNumber = parseInt(numericMatch[1], 10);
      } else {
        const specialMatch = wardNoStr.toString().match(/\d+/);
        if (specialMatch) {
          wardNumber = parseInt(specialMatch[0], 10);
        }
      }
    }
    
    if (!wardNumber && wardNumber !== 0) {
      wardNumber = ward.properties.id || ward.properties.ID;
      if (wardNumber) {
        wardNumber = parseInt(wardNumber, 10);
      }
    }
  }
  
  if (!wardNumber && wardNumber !== 0) {
    wardNumber = ward.id;
    if (wardNumber) {
      wardNumber = parseInt(wardNumber, 10);
    }
  }
  
  if (!wardNumber && wardNumber !== 0) {
    console.warn('Ward number not found for ward:', ward);
    const nameMatch = ward.name?.match(/\d+/);
    if (nameMatch) {
      wardNumber = parseInt(nameMatch[0], 10);
    } else {
      console.error('Cannot determine ward number for:', ward);
      return null;
    }
  }
  
  console.log('=== Weather Data Fetch ===');
  console.log('Ward Name:', ward.name);
  console.log('Extracted Ward Number:', wardNumber);
  
  const today = new Date();
  const dates = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  const weatherPromises = dates.map(date => fetchWeatherForDate(wardNumber, date, city));
  
  try {
    const weatherResults = await Promise.all(weatherPromises);
    console.log(`Received ${weatherResults.length} weather results for ward ${wardNumber}`);
    
    const weatherData = [];
    let successCount = 0;
    let errorCount = 0;
    
    weatherResults.forEach((result, index) => {
      if (result && !result.error && 
          (result.max_temp_c !== null && result.max_temp_c !== undefined ||
           result.min_temp_c !== null && result.min_temp_c !== undefined ||
           result.avg_temp_c !== null && result.avg_temp_c !== undefined)) {
        weatherData.push({
          date: dates[index],
          max_temp_c: result.max_temp_c,
          min_temp_c: result.min_temp_c,
          avg_temp_c: result.avg_temp_c,
          avg_humidity_percent: result.avg_humidity_percent,
          max_feels_like_c: result.max_feels_like_c,
          heat_index: result.heat_index
        });
        successCount++;
      } else {
        errorCount++;
      }
    });
    
    console.log(`Weather data summary: ${successCount} successful, ${errorCount} failed out of ${weatherResults.length} total`);
    
    return weatherData.length > 0 ? weatherData : null;
  } catch (error) {
    console.error('Error fetching weather data:', error);
    return null;
  }
};

// Fetch city-level weather data
export const fetchCityLevelWeatherData = async (city = null) => {
  const sampleWardNumber = 1;
  
  const today = new Date();
  const dates = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  const weatherPromises = dates.map(date => fetchWeatherForDate(sampleWardNumber, date, city));
  
  try {
    const weatherResults = await Promise.all(weatherPromises);
    console.log(`Received ${weatherResults.length} city-level weather results`);
    
    const weatherData = [];
    let successCount = 0;
    let errorCount = 0;
    
    weatherResults.forEach((result, index) => {
      if (result && !result.error && 
          (result.max_temp_c !== null && result.max_temp_c !== undefined ||
           result.min_temp_c !== null && result.min_temp_c !== undefined ||
           result.avg_temp_c !== null && result.avg_temp_c !== undefined)) {
        weatherData.push({
          date: dates[index],
          max_temp_c: result.max_temp_c,
          min_temp_c: result.min_temp_c,
          avg_temp_c: result.avg_temp_c,
          avg_humidity_percent: result.avg_humidity_percent,
          max_feels_like_c: result.max_feels_like_c,
          heat_index: result.heat_index
        });
        successCount++;
      } else {
        errorCount++;
      }
    });
    
    console.log(`City-level weather data: ${successCount} successful, ${errorCount} failed`);
    
    return weatherData.length > 0 ? weatherData : null;
  } catch (error) {
    console.error('Error fetching city-level weather data:', error);
    return null;
  }
};

// Fetch power outage data
export const fetchPowerOutageDataForWards = async (startDate = '2025-05-01', endDate = '2025-05-31', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/power_outages`;
  const startTime = Date.now();
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      headers: getApiHeaders(city)
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.ward_data || result.ward_data.length === 0) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} | No ward data in response | Duration: ${duration}ms`);
      return {};
    }
    
    const wardCount = result.ward_data.length;
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${wardCount} | Date Range: ${startDate} to ${endDate}`);
    
    const wardData = {};
    result.ward_data.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          total_duration_minutes: 0,
          total_consumers_affected: 0,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 },
          record_count: 0
        };
      }
      
      wardData[wardNum].total_duration_minutes += record.total_duration_minutes || 0;
      wardData[wardNum].total_consumers_affected += record.total_consumers_affected || 0;
      
      // Recalculate severity if severity_index is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.severity_index !== undefined && record.severity_index !== null) {
        severityLevel = calculateSeverityLevel(record.severity_index, threshold);
      }

      const normalizedSeverity = String(severityLevel).toUpperCase();
      const effectiveSeverity = normalizedSeverity === 'CRITICAL' ? 'HIGH' : normalizedSeverity;

      wardData[wardNum].severity_counts[effectiveSeverity] = 
        (wardData[wardNum].severity_counts[effectiveSeverity] || 0) + 1;
      wardData[wardNum].record_count += 1;
    });
    
    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      // Check in order: HIGH, MEDIUM, LOW (CRITICAL folded into HIGH)
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });
    
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    // Check if it's a network error
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    // Re-throw the error so calling code can handle it
    throw error;
  }
};

// Fetch power outage trend data
export const fetchPowerOutageTrendData = async (startDate = '2025-05-01', endDate = '2025-05-31', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/power_outages`;
  const startTime = Date.now();
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} (trend) | Date Range: ${startDate} to ${endDate} | Ward: ${wardNumber || 'all'}${city ? ` | City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      headers: getApiHeaders(city)
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.ward_data || result.ward_data.length === 0) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} (trend) | No ward data in response | Duration: ${duration}ms`);
      return null;
    }
    
    let records = result.ward_data;
    if (wardNumber) {
      records = records.filter(record => record.ward_number == wardNumber);
    }
    
    if (records.length === 0) {
      return null;
    }
    
    const dateAggregates = {};
    records.forEach(record => {
      const date = record.outage_date;
      if (!dateAggregates[date]) {
        dateAggregates[date] = {
          total_duration: 0,
          total_consumers: 0,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 }
        };
      }
      dateAggregates[date].total_duration += record.total_duration_minutes || 0;
      dateAggregates[date].total_consumers += record.total_consumers_affected || 0;
      
      // Recalculate severity if severity_index is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.severity_index !== undefined && record.severity_index !== null) {
        severityLevel = calculateSeverityLevel(record.severity_index, threshold);
      }
      
      dateAggregates[date].severity_counts[severityLevel] = 
        (dateAggregates[date].severity_counts[severityLevel] || 0) + 1;
    });
    
    const dateCount = Object.keys(dateAggregates).length;
    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Dates: ${dateCount} | Ward: ${wardNumber || 'all'}`);
    return dateAggregates;
  } catch (error) {
    const duration = Date.now() - startTime;
    // Check if it's a network error
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} (trend) | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    // Re-throw the error so calling code can handle it
    throw error;
  }
};

// Fetch GeoJSON data
export const fetchGeoJSON = async (city) => {
  const endpoint = `/city_json/${city.toLowerCase()}_wards.geojson`;
  const startTime = Date.now();
  
  try {
    const geojsonFile = `${process.env.PUBLIC_URL}${endpoint}`;
    console.log(`[API] 🌐 START: ${endpoint} | City: ${city}`);
    
    const response = await fetch(geojsonFile);
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
    }
    
    const geojson = await response.json();
    
    if (!geojson.features || !Array.isArray(geojson.features)) {
      console.error(`[API] ❌ FAILED: ${endpoint} | Invalid GeoJSON: Missing 'features' array | Duration: ${duration}ms`);
      throw new Error("Invalid GeoJSON: Missing 'features' array.");
    }
    
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Features: ${geojson.features.length}`);
    return geojson;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// Fetch temperature data for all wards
export const fetchTemperatureDataForAllWards = async (geojson, city = null) => {
  console.log('Fetching temperature data for all wards as fallback...');
  
  if (!geojson || !geojson.features) {
    console.warn('GeoJSON not loaded yet');
    return null;
  }
  
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  const wardNumbers = [];
  const wardIndexMap = {};
  
  geojson.features.forEach((feature, index) => {
    let wardNumber = null;
    if (feature.properties) {
      const wardNoStr = feature.properties.ward_number || 
                       feature.properties.Ward_Number || 
                       feature.properties.ward_no || 
                       feature.properties.WARD_NO ||
                       feature.properties.Ward_No;
      if (wardNoStr) {
        const numericMatch = wardNoStr.toString().match(/^(\d+)$/);
        if (numericMatch) {
          wardNumber = parseInt(numericMatch[0], 10);
        } else {
          const specialMatch = wardNoStr.toString().match(/\d+/);
          if (specialMatch) {
            wardNumber = parseInt(specialMatch[0], 10);
          }
        }
      }
    }
    
    if (wardNumber !== null) {
      wardNumbers.push(wardNumber);
      wardIndexMap[wardNumber] = index;
    }
  });
  
  console.log(`Fetching temperature for ${wardNumbers.length} wards`);
  
  const temperaturePromises = wardNumbers.map(wardNumber => 
    fetchWeatherForDate(wardNumber, todayStr, city)
  );
  
  try {
    const results = await Promise.all(temperaturePromises);
    const temperatureMap = {};
    
    results.forEach((result, idx) => {
      const wardNumber = wardNumbers[idx];
      const wardIndex = wardIndexMap[wardNumber];
      
      if (result && !result.error && result.avg_temp_c !== null && result.avg_temp_c !== undefined) {
        temperatureMap[wardIndex] = result.avg_temp_c;
      }
    });
    
    console.log(`Successfully fetched temperature data for ${Object.keys(temperatureMap).length} wards`);
    return temperatureMap;
  } catch (error) {
    console.error('Error fetching temperature data for wards:', error);
    return null;
  }
};

// Fetch severity data for a date range (used for pie charts in TrendContainer)
export const fetchSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/power_outages`;
  const startTime = Date.now();
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} (severity) | Date Range: ${startDate} to ${endDate}${city ? ` | City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      headers: getApiHeaders(city)
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${result.ward_data?.length || 0}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// Fetch power outage forecast for next 7 days
export const fetchPowerOutageForecast = async (startDate) => {
  const endpoint = `/api/forecast/severity/power_outages`;
  const startTime = Date.now();
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      include_ward_details: 'true'
    });
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} | Start Date: ${startDate}`);
    
    const response = await fetch(fullUrl, {
      headers: API_HEADERS
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${result.length || 0}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// Get current system date as YYYY-MM-DD for groundwater forecast (start of 7-day window).
const getTodayDateString = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Fetch groundwater forecast for all wards, next 7 days from current system date.
// - Do not send ward_id so the API returns all wards in the city.
// - start_date: current system date (today).
// - days: 7 (next 7 days including today).
// - include_ward_details: true so response includes ward_predictions.
export const fetchGroundwaterForecast = async (startDateOrOptions, options = {}) => {
  const endpoint = `/api/forecast/severity/groundwater`;
  const startTime = Date.now();
  // Support (startDate) or (options) or () for defaults
  let startDate;
  let opts = options;
  let city = null;
  if (startDateOrOptions == null || typeof startDateOrOptions === 'object') {
    opts = startDateOrOptions || {};
    startDate = opts.start_date || getTodayDateString();
    city = opts.city || null;
  } else {
    startDate = startDateOrOptions;
    city = options.city || null;
  }
  const { days = 7, lookback_years = 5 } = opts;
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      days: String(Math.min(14, Math.max(1, days))),
      lookback_years: String(Math.min(5, Math.max(1, lookback_years))),
      include_ward_details: 'true'
    });
    // Do not add ward_id — omit it to get data for all wards (matches working curl).
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} | Start Date: ${startDate} | days: ${days} | include_ward_details: true${city ? ` | City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      headers: getApiHeaders(city)
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    // Normalize response: backend may use ward_predictions, data, ward_data, or return array
    let wardPredictions = result.ward_predictions;
    if (wardPredictions == null || !Array.isArray(wardPredictions)) {
      if (Array.isArray(result.data)) wardPredictions = result.data;
      else if (Array.isArray(result.ward_data)) wardPredictions = result.ward_data;
      else if (Array.isArray(result)) wardPredictions = result;
      else wardPredictions = [];
    }
    const normalized = {
      ...result,
      ward_predictions: wardPredictions,
      daily_summary: result.daily_summary || []
    };
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${wardPredictions.length}`);
    console.log(`[API] Response structure:`, {
      indicator: normalized.indicator,
      hasDailySummary: !!normalized.daily_summary?.length,
      dailySummaryLength: normalized.daily_summary?.length,
      hasWardPredictions: true,
      wardPredictionsLength: normalized.ward_predictions?.length,
      sampleWard: normalized.ward_predictions?.[0]
    });
    return normalized;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Groundwater API ───────────────────────────────────────────────────────
// Data period: 01/01/2020 to 31/12/2025
// Record shape: { ward_name, city_name, measurement_date, severity_index, severity_level, current_gwl_meters, mean_gwl_meters, depletion_level_meters }

export const fetchGroundwaterDataForWards = async (startDate = '2020-01-01', endDate = '2025-12-31', threshold = 0.5, city = null) => {
  const endpoint = `/api/dashboard/severity/groundwater`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardDataByKey = {};
    records.forEach((record) => {
      const key = record.ward_number ?? record.ward_name;
      if (key == null) return;
      const depletion = Number(record.depletion_level_meters);
      const severity = calculateGroundwaterSeverityLevel(isNaN(depletion) ? 0 : depletion, threshold);
      if (!wardDataByKey[key]) {
        wardDataByKey[key] = {
          ward_number: record.ward_number,
          ward_name: record.ward_name,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          latest_depletion_level_meters: record.depletion_level_meters,
          latest_current_gwl_meters: record.current_gwl_meters,
          latest_severity_index: record.severity_index,
          record_count: 0
        };
      }
      wardDataByKey[key].severity_counts[severity] = (wardDataByKey[key].severity_counts[severity] || 0) + 1;
      wardDataByKey[key].latest_depletion_level_meters = record.depletion_level_meters;
      wardDataByKey[key].latest_current_gwl_meters = record.current_gwl_meters;
      wardDataByKey[key].latest_severity_index = record.severity_index;
      wardDataByKey[key].record_count += 1;
    });

    Object.keys(wardDataByKey).forEach((key) => {
      const counts = wardDataByKey[key].severity_counts;
      wardDataByKey[key].dominant_severity =
        counts.HIGH > 0 ? 'HIGH' : counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardDataByKey).length}`);
    return wardDataByKey;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchGroundwaterTrendData = async (startDate = '2020-01-01', endDate = '2025-12-31', wardKey = null, mode = 'gwl', city = null) => {
  const endpoint = `/api/dashboard/severity/groundwater`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardKey != null) {
      records = records.filter((r) => (r.ward_number != null && r.ward_number === wardKey) || (r.ward_name != null && String(r.ward_name) === String(wardKey)));
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const byDate = {};
    records.forEach((record) => {
      const d = record.measurement_date;
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], gwl: [], severity_index: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].gwl.push(Number(record.current_gwl_meters));
      byDate[d].severity_index.push(Number(record.severity_index));
    });
    const dates = Object.keys(byDate).sort();
    const trendData = {
      dates,
      gwl: dates.map((d) => {
        const arr = byDate[d].gwl.filter((v) => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      severity_index: dates.map((d) => {
        const arr = byDate[d].severity_index.filter((v) => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };
    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchGroundwaterSeverityDataForDateRange = async (startDate, endDate, threshold = 0.5, city = null) => {
  const endpoint = `/api/dashboard/severity/groundwater`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Informal Workers API ───────────────────────────────────────────────────────
// Data for "No show rate among workers" and "Daily wage loss" indicators
// API endpoint: /api/dashboard/severity/informal_workers
// Returns: no_show_rate_severity, total_registered_e_shram, total_heat_illness, informal_income_loss_severity

export const fetchInformalWorkersDataForWards = async (startDate = '2026-01-01', endDate = '2026-02-04', indicatorType = 'no_show_rate', city = null) => {
  const endpoint = `/api/dashboard/severity/informal_workers`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Indicator: ${indicatorType}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.ward_data || result.ward_data.length === 0) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} | No ward data in response | Duration: ${duration}ms`);
      return {};
    }

    const wardCount = result.ward_data.length;
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${wardCount} | Date Range: ${startDate} to ${endDate}`);

    const wardData = {};
    result.ward_data.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          no_show_rate_severity: record.no_show_rate_severity,
          informal_income_loss_severity: record.informal_income_loss_severity,
          total_registered_e_shram: record.total_registered_e_shram,
          total_heat_illness: record.total_heat_illness,
          record_count: 0
        };
      }

      // Update with latest values if multiple records exist
      if (record.no_show_rate_severity !== undefined && record.no_show_rate_severity !== null) {
        wardData[wardNum].no_show_rate_severity = record.no_show_rate_severity;
      }
      if (record.informal_income_loss_severity !== undefined && record.informal_income_loss_severity !== null) {
        wardData[wardNum].informal_income_loss_severity = record.informal_income_loss_severity;
      }
      if (record.total_registered_e_shram !== undefined && record.total_registered_e_shram !== null) {
        wardData[wardNum].total_registered_e_shram = record.total_registered_e_shram;
      }
      if (record.total_heat_illness !== undefined && record.total_heat_illness !== null) {
        wardData[wardNum].total_heat_illness = record.total_heat_illness;
      }
      wardData[wardNum].record_count += 1;
    });

    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    throw error;
  }
};

export const fetchInformalWorkersTrendData = async (startDate = '2026-01-01', endDate = '2026-02-04', wardNumber = null, indicatorType = 'no_show_rate', city = null) => {
  const endpoint = `/api/dashboard/severity/informal_workers`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} (trend) | Date Range: ${startDate} to ${endDate} | Ward: ${wardNumber || 'all'} | Indicator: ${indicatorType}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.ward_data || result.ward_data.length === 0) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} (trend) | No ward data in response | Duration: ${duration}ms`);
      return null;
    }

    let records = result.ward_data;
    if (wardNumber) {
      records = records.filter(record => record.ward_number == wardNumber);
    }

    if (records.length === 0) {
      return null;
    }

    const dateAggregates = {};
    records.forEach(record => {
      const date = record.date || record.measurement_date || record.outage_date;
      if (!date) return;
      
      if (!dateAggregates[date]) {
        dateAggregates[date] = {
          no_show_rate_severity: [],
          informal_income_loss_severity: [],
          total_registered_e_shram: [],
          total_heat_illness: []
        };
      }

      if (record.no_show_rate_severity !== undefined && record.no_show_rate_severity !== null) {
        dateAggregates[date].no_show_rate_severity.push(Number(record.no_show_rate_severity));
      }
      if (record.informal_income_loss_severity !== undefined && record.informal_income_loss_severity !== null) {
        dateAggregates[date].informal_income_loss_severity.push(Number(record.informal_income_loss_severity));
      }
      if (record.total_registered_e_shram !== undefined && record.total_registered_e_shram !== null) {
        dateAggregates[date].total_registered_e_shram.push(Number(record.total_registered_e_shram));
      }
      if (record.total_heat_illness !== undefined && record.total_heat_illness !== null) {
        dateAggregates[date].total_heat_illness.push(Number(record.total_heat_illness));
      }
    });

    const dates = Object.keys(dateAggregates).sort();
    const trendData = {
      dates,
      no_show_rate_severity: dates.map(d => {
        const arr = dateAggregates[d].no_show_rate_severity.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      informal_income_loss_severity: dates.map(d => {
        const arr = dateAggregates[d].informal_income_loss_severity.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      total_registered_e_shram: dates.map(d => {
        const arr = dateAggregates[d].total_registered_e_shram.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      }),
      total_heat_illness: dates.map(d => {
        const arr = dateAggregates[d].total_heat_illness.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      })
    };

    const dateCount = Object.keys(dateAggregates).length;
    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Dates: ${dateCount} | Ward: ${wardNumber || 'all'}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} (trend) | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    throw error;
  }
};

export const fetchInformalWorkersSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/informal_workers`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} (severity) | Date Range: ${startDate} to ${endDate}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${result.ward_data?.length || 0}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Construction Labor API ───────────────────────────────────────────────────────
// Data for "Lost labor hours due to heat stress", "% of workers without access to shade/water", 
// and "Reported workplace heat illness cases" indicators
// API endpoint: /api/dashboard/severity/construction_labour
// Returns: lost_hour_severity_pct, no_shade_water_severity_pct, heat_illness_severity_pct, total_workers_present

export const fetchConstructionLaborDataForWards = async (startDate = '2025-05-01', endDate = '2026-02-04', indicatorType = 'lost_hours', city = null) => {
  const endpoint = `/api/dashboard/severity/construction_labour`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Indicator: ${indicatorType}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.ward_data || result.ward_data.length === 0) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} | No ward data in response | Duration: ${duration}ms`);
      return {};
    }

    const wardCount = result.ward_data.length;
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${wardCount} | Date Range: ${startDate} to ${endDate}`);

    const wardData = {};
    result.ward_data.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          lost_hour_severity_pct: record.lost_hour_severity_pct,
          no_shade_water_severity_pct: record.no_shade_water_severity_pct,
          heat_illness_severity_pct: record.heat_illness_severity_pct,
          total_workers_present: record.total_workers_present,
          record_count: 0
        };
      }

      // Update with latest values if multiple records exist
      if (record.lost_hour_severity_pct !== undefined && record.lost_hour_severity_pct !== null) {
        wardData[wardNum].lost_hour_severity_pct = record.lost_hour_severity_pct;
      }
      if (record.no_shade_water_severity_pct !== undefined && record.no_shade_water_severity_pct !== null) {
        wardData[wardNum].no_shade_water_severity_pct = record.no_shade_water_severity_pct;
      }
      if (record.heat_illness_severity_pct !== undefined && record.heat_illness_severity_pct !== null) {
        wardData[wardNum].heat_illness_severity_pct = record.heat_illness_severity_pct;
      }
      if (record.total_workers_present !== undefined && record.total_workers_present !== null) {
        wardData[wardNum].total_workers_present = record.total_workers_present;
      }
      wardData[wardNum].record_count += 1;
    });

    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    throw error;
  }
};

export const fetchConstructionLaborTrendData = async (startDate = '2025-05-01', endDate = '2026-02-04', wardNumber = null, indicatorType = 'lost_hours', city = null) => {
  const endpoint = `/api/dashboard/severity/construction_labour`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} (trend) | Date Range: ${startDate} to ${endDate} | Ward: ${wardNumber || 'all'} | Indicator: ${indicatorType}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.ward_data || result.ward_data.length === 0) {
      console.warn(`[API] ⚠️  WARNING: ${endpoint} (trend) | No ward data in response | Duration: ${duration}ms`);
      return null;
    }

    let records = result.ward_data;
    if (wardNumber) {
      records = records.filter(record => record.ward_number == wardNumber);
    }

    if (records.length === 0) {
      return null;
    }

    const dateAggregates = {};
    records.forEach(record => {
      const date = record.date || record.measurement_date || record.outage_date;
      if (!date) return;
      
      if (!dateAggregates[date]) {
        dateAggregates[date] = {
          lost_hour_severity_pct: [],
          no_shade_water_severity_pct: [],
          heat_illness_severity_pct: [],
          total_workers_present: []
        };
      }

      if (record.lost_hour_severity_pct !== undefined && record.lost_hour_severity_pct !== null) {
        dateAggregates[date].lost_hour_severity_pct.push(Number(record.lost_hour_severity_pct));
      }
      if (record.no_shade_water_severity_pct !== undefined && record.no_shade_water_severity_pct !== null) {
        dateAggregates[date].no_shade_water_severity_pct.push(Number(record.no_shade_water_severity_pct));
      }
      if (record.heat_illness_severity_pct !== undefined && record.heat_illness_severity_pct !== null) {
        dateAggregates[date].heat_illness_severity_pct.push(Number(record.heat_illness_severity_pct));
      }
      if (record.total_workers_present !== undefined && record.total_workers_present !== null) {
        dateAggregates[date].total_workers_present.push(Number(record.total_workers_present));
      }
    });

    const dates = Object.keys(dateAggregates).sort();
    const trendData = {
      dates,
      lost_hour_severity_pct: dates.map(d => {
        const arr = dateAggregates[d].lost_hour_severity_pct.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      no_shade_water_severity_pct: dates.map(d => {
        const arr = dateAggregates[d].no_shade_water_severity_pct.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      heat_illness_severity_pct: dates.map(d => {
        const arr = dateAggregates[d].heat_illness_severity_pct.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      total_workers_present: dates.map(d => {
        const arr = dateAggregates[d].total_workers_present.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      })
    };

    const dateCount = Object.keys(dateAggregates).length;
    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Dates: ${dateCount} | Ward: ${wardNumber || 'all'}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} (trend) | Cannot reach API server at ${API_BASE_URL} | Duration: ${duration}ms`);
      console.error(`[API] Check if API server is running and database connection is active`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    console.error(`[API] Stack trace:`, error.stack);
    throw error;
  }
};

export const fetchConstructionLaborSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/construction_labour`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} (severity) | Date Range: ${startDate} to ${endDate}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${result.ward_data?.length || 0}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Forecast APIs ───────────────────────────────────────────────────────

// Fetch informal workers forecast for next 7 days
export const fetchInformalWorkersForecast = async (startDateOrOptions, options = {}) => {
  const endpoint = `/api/forecast/severity/informal_workers`;
  const startTime = Date.now();
  
  // Support (startDate) or (options) or () for defaults
  let startDate;
  let opts = options;
  let city = null;
  if (startDateOrOptions == null || typeof startDateOrOptions === 'object') {
    opts = startDateOrOptions || {};
    startDate = opts.start_date || getTodayDateString();
    city = opts.city || null;
  } else {
    startDate = startDateOrOptions;
    city = options.city || null;
  }
  const { days = 7, lookback_years = 5 } = opts;
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      days: String(Math.min(14, Math.max(1, days))),
      lookback_years: String(Math.min(5, Math.max(1, lookback_years))),
      include_ward_details: 'true'
    });
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} | Start Date: ${startDate} | days: ${days} | include_ward_details: true${city ? ` | City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      headers: getApiHeaders(city)
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    // Normalize response: backend may use ward_predictions, data, ward_data, or return array
    let wardPredictions = result.ward_predictions;
    if (wardPredictions == null || !Array.isArray(wardPredictions)) {
      if (Array.isArray(result.data)) wardPredictions = result.data;
      else if (Array.isArray(result.ward_data)) wardPredictions = result.ward_data;
      else if (Array.isArray(result)) wardPredictions = result;
      else wardPredictions = [];
    }
    const normalized = {
      ...result,
      ward_predictions: wardPredictions,
      daily_summary: result.daily_summary || []
    };
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${wardPredictions.length}`);
    return normalized;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// Fetch construction labor forecast for next 7 days
export const fetchConstructionLaborForecast = async (startDateOrOptions, options = {}) => {
  const endpoint = `/api/forecast/severity/construction_labour`;
  const startTime = Date.now();
  
  // Support (startDate) or (options) or () for defaults
  let startDate;
  let opts = options;
  let city = null;
  if (startDateOrOptions == null || typeof startDateOrOptions === 'object') {
    opts = startDateOrOptions || {};
    startDate = opts.start_date || getTodayDateString();
    city = opts.city || null;
  } else {
    startDate = startDateOrOptions;
    city = options.city || null;
  }
  const { days = 7, lookback_years = 5 } = opts;
  
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      days: String(Math.min(14, Math.max(1, days))),
      lookback_years: String(Math.min(5, Math.max(1, lookback_years))),
      include_ward_details: 'true'
    });
    const fullUrl = `${url}?${params}`;
    
    console.log(`[API] 🌐 START: ${endpoint} | Start Date: ${startDate} | days: ${days} | include_ward_details: true${city ? ` | City: ${city}` : ''}`);
    
    const response = await fetch(fullUrl, {
      headers: getApiHeaders(city)
    });
    
    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = response.statusText;
      }
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} ${response.statusText} | Duration: ${duration}ms`);
      console.error(`[API] Error response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    // Normalize response: backend may use ward_predictions, data, ward_data, or return array
    let wardPredictions = result.ward_predictions;
    if (wardPredictions == null || !Array.isArray(wardPredictions)) {
      if (Array.isArray(result.data)) wardPredictions = result.data;
      else if (Array.isArray(result.ward_data)) wardPredictions = result.ward_data;
      else if (Array.isArray(result)) wardPredictions = result;
      else wardPredictions = [];
    }
    const normalized = {
      ...result,
      ward_predictions: wardPredictions,
      daily_summary: result.daily_summary || []
    };
    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${wardPredictions.length}`);
    return normalized;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Transport Disruptions API ───────────────────────────────────────────────────────
// Data for "Public transport disruption (rail, metro, bus)" indicator
// API endpoint: /api/dashboard/severity/transport_disruptions
// Returns: severity_index, commuters_affected, disruption_minutes

export const fetchTransportDisruptionsDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/transport_disruptions`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          severity_index: record.severity_index,
          commuters_affected: record.commuters_affected,
          disruption_minutes: record.disruption_minutes,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if disruption_minutes is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.disruption_minutes !== undefined && record.disruption_minutes !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.5, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 30, '>'], [threshold[1] || 30, '<'], [threshold[2] || 15, '<']];
        }
        severityLevel = calculateTransportDisruptionsSeverityLevel(
          record.disruption_minutes,
          thresholds[0] || [30, '>'],
          thresholds[1] || [30, '<'],
          thresholds[2] || [15, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].severity_index = record.severity_index;
      wardData[wardNum].commuters_affected = record.commuters_affected;
      wardData[wardNum].disruption_minutes = record.disruption_minutes;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchTransportDisruptionsTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/transport_disruptions`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    // Normalize date to YYYY-MM-DD for consistent grouping (handles datetime strings or alternate field names)
    const toDateKey = (val) => {
      if (val == null || val === '') return null;
      const s = String(val).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const parsed = new Date(s);
      if (!isNaN(parsed.getTime())) {
        const y = parsed.getFullYear();
        const m = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }
      return null;
    };

    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(
        record.date ||
        record.measurement_date ||
        record.outage_date ||
        record.disruption_date ||
        record.record_date
      );
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], severity_index: [], commuters_affected: [], disruption_minutes: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].severity_index.push(Number(record.severity_index));
      byDate[d].commuters_affected.push(Number(record.commuters_affected));
      byDate[d].disruption_minutes.push(Number(record.disruption_minutes));
    });

    let dates = Object.keys(byDate).sort();

    // If backend returns records but no date field (e.g. aggregated per ward), aggregate into one point for the range so bars still show
    if (dates.length === 0 && records.length > 0) {
      const firstRecord = records[0];
      const rangeStart = toDateKey(firstRecord.date || firstRecord.measurement_date || firstRecord.outage_date) || startDate;
      dates = [rangeStart];
      const commuters = records.map(r => Number(r.commuters_affected)).filter(v => !isNaN(v));
      const minutes = records.map(r => Number(r.disruption_minutes)).filter(v => !isNaN(v));
      const severity = records.map(r => Number(r.severity_index)).filter(v => !isNaN(v));
      byDate[rangeStart] = {
        dates: [rangeStart],
        severity_index: severity.length ? [severity.reduce((a, b) => a + b, 0) / severity.length] : [],
        commuters_affected: [commuters.reduce((a, b) => a + b, 0)],
        disruption_minutes: minutes.length ? [minutes.reduce((a, b) => a + b, 0) / minutes.length] : []
      };
    }

    const trendData = {
      dates,
      severity_index: dates.map(d => {
        const arr = (byDate[d]?.severity_index || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      commuters_affected: dates.map(d => {
        const arr = (byDate[d]?.commuters_affected || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      }),
      disruption_minutes: dates.map(d => {
        const arr = (byDate[d]?.disruption_minutes || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchTransportDisruptionsSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/transport_disruptions`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Road Closures API ───────────────────────────────────────────────────────
// Data for "Road/bridge degradation or closures" indicator
// API endpoint: /api/dashboard/severity/road_closures
// Returns: severity_index

export const fetchRoadClosuresDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/road_closures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          severity_index: record.severity_index,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if severity_index is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.severity_index !== undefined && record.severity_index !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.5, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 2, '>'], [threshold[1] || 2, '<'], [threshold[2] || 1, '<']];
        }
        severityLevel = calculateRoadClosuresSeverityLevel(
          record.severity_index,
          thresholds[0] || [2, '>'],
          thresholds[1] || [2, '<'],
          thresholds[2] || [1, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].severity_index = record.severity_index;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchRoadClosuresTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/road_closures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const byDate = {};
    records.forEach(record => {
      const d = record.date || record.measurement_date || record.outage_date;
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], severity_index: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].severity_index.push(Number(record.severity_index));
    });

    const dates = Object.keys(byDate).sort();
    const trendData = {
      dates,
      severity_index: dates.map(d => {
        const arr = byDate[d].severity_index.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchRoadClosuresSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/road_closures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Traffic Signal Failures API ───────────────────────────────────────────────────────
// Data for "Traffic signal/equipment failures" indicator
// API endpoint: /api/dashboard/severity/traffic_signal_failures
// Returns: total_duration_minutes

export const fetchTrafficSignalFailuresDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/traffic_signal_failures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          total_duration_minutes: record.total_duration_minutes,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if total_duration_minutes is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.total_duration_minutes !== undefined && record.total_duration_minutes !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.17, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 30, '>'], [threshold[1] || 30, '<'], [threshold[2] || 5, '<']];
        }
        severityLevel = calculateTrafficSignalFailuresSeverityLevel(
          record.total_duration_minutes,
          thresholds[0] || [30, '>'],
          thresholds[1] || [30, '<'],
          thresholds[2] || [5, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].total_duration_minutes = record.total_duration_minutes;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchTrafficSignalFailuresTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/traffic_signal_failures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const byDate = {};
    records.forEach(record => {
      const d = record.date || record.measurement_date || record.outage_date;
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], total_duration_minutes: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].total_duration_minutes.push(Number(record.total_duration_minutes));
    });

    const dates = Object.keys(byDate).sort();
    const trendData = {
      dates,
      total_duration_minutes: dates.map(d => {
        const arr = byDate[d].total_duration_minutes.filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchTrafficSignalFailuresSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/traffic_signal_failures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Cooling System Failures API ───────────────────────────────────────────────────────
// Data for "Cooling system failure in public buildings" indicator
// API endpoint: /api/dashboard/severity/cooling_system_failures
// Returns: severity_index, total_people_affected, total_cooling_capacity_affected

export const fetchCoolingSystemFailuresDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/cooling_system_failures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          severity_index: record.severity_index,
          total_people_affected: record.total_people_affected,
          total_cooling_capacity_affected: record.total_cooling_capacity_affected,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if total_cooling_capacity_affected is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.total_cooling_capacity_affected !== undefined && record.total_cooling_capacity_affected !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.11, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 45, '>'], [threshold[1] || 45, '<'], [threshold[2] || 5, '<']];
        }
        const isHospital = record.building_type === 'hospital' || record.is_hospital === true;
        severityLevel = calculateCoolingSystemFailuresSeverityLevel(
          record.total_cooling_capacity_affected,
          isHospital,
          thresholds[0] || [45, '>'],
          thresholds[1] || [45, '<'],
          thresholds[2] || [5, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].severity_index = record.severity_index;
      wardData[wardNum].total_people_affected = record.total_people_affected;
      wardData[wardNum].total_cooling_capacity_affected = record.total_cooling_capacity_affected;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchCoolingSystemFailuresTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/cooling_system_failures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], severity_index: [], total_people_affected: [], total_cooling_capacity_affected: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].severity_index.push(Number(record.severity_index));
      byDate[d].total_people_affected.push(Number(record.total_people_affected));
      byDate[d].total_cooling_capacity_affected.push(Number(record.total_cooling_capacity_affected));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        severity_index: records.map(r => Number(r.severity_index)).filter(v => !isNaN(v)),
        total_people_affected: records.map(r => Number(r.total_people_affected)).filter(v => !isNaN(v)),
        total_cooling_capacity_affected: records.map(r => Number(r.total_cooling_capacity_affected)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      severity_index: dates.map(d => {
        const arr = (byDate[d]?.severity_index || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      total_people_affected: dates.map(d => {
        const arr = (byDate[d]?.total_people_affected || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      }),
      total_cooling_capacity_affected: dates.map(d => {
        const arr = (byDate[d]?.total_cooling_capacity_affected || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchCoolingSystemFailuresSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/cooling_system_failures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── School Closures API ───────────────────────────────────────────────────────
// Data for "No. of school/college closure days due to heat" indicator
// API endpoint: /api/dashboard/severity/school_closures
// Returns: school_closures_severity, school_closures_severity_ward_wise, total_schools_in_ward

export const fetchSchoolClosuresDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/school_closures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          school_closures_severity: record.school_closures_severity,
          school_closures_severity_ward_wise: record.school_closures_severity_ward_wise,
          total_schools_in_ward: record.total_schools_in_ward,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if total_schools_in_ward is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.total_schools_in_ward !== undefined && record.total_schools_in_ward !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.28, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 18, '>'], [threshold[1] || 18, '<'], [threshold[2] || 5, '<']];
        }
        severityLevel = calculateSchoolClosuresSeverityLevel(
          record.total_schools_in_ward,
          thresholds[0] || [18, '>'],
          thresholds[1] || [18, '<'],
          thresholds[2] || [5, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].school_closures_severity = record.school_closures_severity;
      wardData[wardNum].school_closures_severity_ward_wise = record.school_closures_severity_ward_wise;
      wardData[wardNum].total_schools_in_ward = record.total_schools_in_ward;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchSchoolClosuresTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/school_closures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], school_closures_severity: [], school_closures_severity_ward_wise: [], total_schools_in_ward: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].school_closures_severity.push(Number(record.school_closures_severity));
      byDate[d].school_closures_severity_ward_wise.push(Number(record.school_closures_severity_ward_wise));
      byDate[d].total_schools_in_ward.push(Number(record.total_schools_in_ward));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        school_closures_severity: records.map(r => Number(r.school_closures_severity)).filter(v => !isNaN(v)),
        school_closures_severity_ward_wise: records.map(r => Number(r.school_closures_severity_ward_wise)).filter(v => !isNaN(v)),
        total_schools_in_ward: records.map(r => Number(r.total_schools_in_ward)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      school_closures_severity: dates.map(d => {
        const arr = (byDate[d]?.school_closures_severity || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      school_closures_severity_ward_wise: dates.map(d => {
        const arr = (byDate[d]?.school_closures_severity_ward_wise || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      total_schools_in_ward: dates.map(d => {
        const arr = (byDate[d]?.total_schools_in_ward || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchSchoolClosuresSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/school_closures`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── School Cooling API ───────────────────────────────────────────────────────
// Data for "% of schools without basic cooling infrastructure" indicator
// API endpoint: /api/dashboard/severity/school_cooling
// Returns: cooling_infra_severity, classrooms_with_cooling_infra, total_classrooms_in_ward

export const fetchSchoolCoolingDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/school_cooling`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          cooling_infra_severity: record.cooling_infra_severity,
          classrooms_with_cooling_infra: record.classrooms_with_cooling_infra,
          total_classrooms_in_ward: record.total_classrooms_in_ward,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity based on percentage without cooling infrastructure
      let severityLevel = record.severity_level;
      if (threshold !== null && record.classrooms_with_cooling_infra !== undefined && record.total_classrooms_in_ward !== undefined && record.total_classrooms_in_ward > 0) {
        const percentageWithoutCooling = ((record.total_classrooms_in_ward - record.classrooms_with_cooling_infra) / record.total_classrooms_in_ward) * 100;
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.95, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 95, '>'], [threshold[1] || 95, '<'], [threshold[2] || 90, '<']];
        }
        severityLevel = calculateSchoolCoolingSeverityLevel(
          percentageWithoutCooling,
          thresholds[0] || [95, '>'],
          thresholds[1] || [95, '<'],
          thresholds[2] || [90, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].cooling_infra_severity = record.cooling_infra_severity;
      wardData[wardNum].classrooms_with_cooling_infra = record.classrooms_with_cooling_infra;
      wardData[wardNum].total_classrooms_in_ward = record.total_classrooms_in_ward;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchSchoolCoolingTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/school_cooling`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], cooling_infra_severity: [], classrooms_with_cooling_infra: [], total_classrooms_in_ward: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].cooling_infra_severity.push(Number(record.cooling_infra_severity));
      byDate[d].classrooms_with_cooling_infra.push(Number(record.classrooms_with_cooling_infra));
      byDate[d].total_classrooms_in_ward.push(Number(record.total_classrooms_in_ward));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        cooling_infra_severity: records.map(r => Number(r.cooling_infra_severity)).filter(v => !isNaN(v)),
        classrooms_with_cooling_infra: records.map(r => Number(r.classrooms_with_cooling_infra)).filter(v => !isNaN(v)),
        total_classrooms_in_ward: records.map(r => Number(r.total_classrooms_in_ward)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      cooling_infra_severity: dates.map(d => {
        const arr = (byDate[d]?.cooling_infra_severity || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      classrooms_with_cooling_infra: dates.map(d => {
        const arr = (byDate[d]?.classrooms_with_cooling_infra || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      }),
      total_classrooms_in_ward: dates.map(d => {
        const arr = (byDate[d]?.total_classrooms_in_ward || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchSchoolCoolingSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/school_cooling`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Water Supply API ───────────────────────────────────────────────────────
// Data for "Drop in per capita water availability (lpcd)" indicator
// API endpoint: /api/dashboard/severity/water_supply
// Returns: drop_ratio, daily_demand_lpcd, daily_supply_lpcd

export const fetchWaterSupplyDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/water_supply`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          drop_ratio: record.drop_ratio,
          daily_demand_lpcd: record.daily_demand_lpcd,
          daily_supply_lpcd: record.daily_supply_lpcd,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if drop_ratio is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.drop_ratio !== undefined && record.drop_ratio !== null) {
        const thresholds = Array.isArray(threshold) ? threshold : [threshold, threshold, threshold * 0.25];
        severityLevel = calculateWaterSupplySeverityLevel(
          record.drop_ratio,
          thresholds[0] || 0.4,
          thresholds[1] || 0.4,
          thresholds[2] || 0.1
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].drop_ratio = record.drop_ratio;
      wardData[wardNum].daily_demand_lpcd = record.daily_demand_lpcd;
      wardData[wardNum].daily_supply_lpcd = record.daily_supply_lpcd;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchWaterSupplyTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/water_supply`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], drop_ratio: [], daily_demand_lpcd: [], daily_supply_lpcd: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].drop_ratio.push(Number(record.drop_ratio));
      byDate[d].daily_demand_lpcd.push(Number(record.daily_demand_lpcd));
      byDate[d].daily_supply_lpcd.push(Number(record.daily_supply_lpcd));
    });

    let dates = Object.keys(byDate).sort();

    // When records exist but no date field (e.g. aggregated), use range start so bar chart can show one aggregated value
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = startDate.substring(0, 10);
      dates = [rangeStart];
      const demand = records.map(r => Number(r.daily_demand_lpcd)).filter(v => !isNaN(v));
      const supply = records.map(r => Number(r.daily_supply_lpcd)).filter(v => !isNaN(v));
      const ratio = records.map(r => Number(r.drop_ratio)).filter(v => !isNaN(v));
      byDate[rangeStart] = {
        dates: [rangeStart],
        drop_ratio: ratio.length ? [ratio.reduce((a, b) => a + b, 0) / ratio.length] : [],
        daily_demand_lpcd: demand.length ? [demand.reduce((a, b) => a + b, 0)] : [],
        daily_supply_lpcd: supply.length ? [supply.reduce((a, b) => a + b, 0)] : []
      };
    }

    const trendData = {
      dates,
      drop_ratio: dates.map(d => {
        const arr = (byDate[d]?.drop_ratio || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      daily_demand_lpcd: dates.map(d => {
        const arr = (byDate[d]?.daily_demand_lpcd || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      }),
      daily_supply_lpcd: dates.map(d => {
        const arr = (byDate[d]?.daily_supply_lpcd || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchWaterSupplySeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/water_supply`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Water Tankers API ───────────────────────────────────────────────────────
// Data for "Percentage of wards dependent on water tankers" indicator
// API endpoint: /api/dashboard/severity/water_tankers
// Returns: severity_index, tanker_trips, emergency_requests

export const fetchWaterTankersDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/water_tankers`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          severity_index: record.severity_index,
          tanker_trips: record.tanker_trips,
          emergency_requests: record.emergency_requests,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if emergency_requests is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.emergency_requests !== undefined && record.emergency_requests !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.25, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 0.4, '>'], [threshold[1] || 0.4, '<'], [threshold[2] || 0.1, '<']];
        }
        severityLevel = calculateWaterTankersSeverityLevel(
          record.emergency_requests,
          thresholds[0] || [0.4, '>'],
          thresholds[1] || [0.4, '<'],
          thresholds[2] || [0.1, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].severity_index = record.severity_index;
      wardData[wardNum].tanker_trips = record.tanker_trips;
      wardData[wardNum].emergency_requests = record.emergency_requests;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchWaterTankersTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/water_tankers`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], severity_index: [], tanker_trips: [], emergency_requests: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].severity_index.push(Number(record.severity_index));
      byDate[d].tanker_trips.push(Number(record.tanker_trips));
      byDate[d].emergency_requests.push(Number(record.emergency_requests));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        severity_index: records.map(r => Number(r.severity_index)).filter(v => !isNaN(v)),
        tanker_trips: records.map(r => Number(r.tanker_trips)).filter(v => !isNaN(v)),
        emergency_requests: records.map(r => Number(r.emergency_requests)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      severity_index: dates.map(d => {
        const arr = (byDate[d]?.severity_index || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      tanker_trips: dates.map(d => {
        const arr = (byDate[d]?.tanker_trips || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      }),
      emergency_requests: dates.map(d => {
        const arr = (byDate[d]?.emergency_requests || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchWaterTankersSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/water_tankers`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Heat Differential API ───────────────────────────────────────────────────────
// Data for "Temperature differential (urban vs. rural)" indicator
// API endpoint: /api/dashboard/severity/heat_differential
// Returns: severity_index, avg_urban_temp_c, avg_rural_temp_c

export const fetchHeatDifferentialDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/heat_differential`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          severity_index: record.severity_index,
          avg_urban_temp_c: record.avg_urban_temp_c,
          avg_rural_temp_c: record.avg_rural_temp_c,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity based on temperature difference
      let severityLevel = record.severity_level;
      if (threshold !== null && record.avg_urban_temp_c !== undefined && record.avg_rural_temp_c !== undefined) {
        const tempDiff = Math.abs(record.avg_urban_temp_c - record.avg_rural_temp_c);
        const thresholds = Array.isArray(threshold) ? threshold : [threshold, threshold, threshold * 0.33];
        severityLevel = calculateHeatDifferentialSeverityLevel(
          tempDiff,
          thresholds[0] || 6,
          thresholds[1] || 6,
          thresholds[2] || 2
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].severity_index = record.severity_index;
      wardData[wardNum].avg_urban_temp_c = record.avg_urban_temp_c;
      wardData[wardNum].avg_rural_temp_c = record.avg_rural_temp_c;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchHeatDifferentialTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/heat_differential`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], severity_index: [], avg_urban_temp_c: [], avg_rural_temp_c: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].severity_index.push(Number(record.severity_index));
      byDate[d].avg_urban_temp_c.push(Number(record.avg_urban_temp_c));
      byDate[d].avg_rural_temp_c.push(Number(record.avg_rural_temp_c));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        severity_index: records.map(r => Number(r.severity_index)).filter(v => !isNaN(v)),
        avg_urban_temp_c: records.map(r => Number(r.avg_urban_temp_c)).filter(v => !isNaN(v)),
        avg_rural_temp_c: records.map(r => Number(r.avg_rural_temp_c)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      severity_index: dates.map(d => {
        const arr = (byDate[d]?.severity_index || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      avg_urban_temp_c: dates.map(d => {
        const arr = (byDate[d]?.avg_urban_temp_c || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      avg_rural_temp_c: dates.map(d => {
        const arr = (byDate[d]?.avg_rural_temp_c || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchHeatDifferentialSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/heat_differential`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Surface Temperature API ───────────────────────────────────────────────────────
// Data for "Surface temperature & low vegetation zones" indicator
// API endpoint: /api/dashboard/severity/surface_temperature
// Returns: vegetation_index, surface_temp_c, land_use_type
// Special: Uses dual thresholds (NDVI and LST) for severity calculation

export const fetchSurfaceTemperatureDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/surface_temperature`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          vegetation_index: record.vegetation_index,
          surface_temp_c: record.surface_temp_c,
          land_use_type: record.land_use_type,
          ndvi: record.ndvi,
          lst: record.lst,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity using dual thresholds (NDVI and LST)
      let severityLevel = record.severity_level;
      if (threshold !== null && record.ndvi !== undefined && record.lst !== undefined) {
        // threshold format: { highNdvi: [value, operator], highLst: [value, operator], ... }
        let thresholds;
        if (typeof threshold === 'object' && threshold !== null && threshold.highNdvi !== undefined) {
          // New format with operators
          thresholds = {
            highNdvi: Array.isArray(threshold.highNdvi) ? threshold.highNdvi : [threshold.highNdvi || 0.1, '<'],
            highLst: Array.isArray(threshold.highLst) ? threshold.highLst : [threshold.highLst || 45, '>'],
            mediumNdvi: Array.isArray(threshold.mediumNdvi) ? threshold.mediumNdvi : [threshold.mediumNdvi || 0.1, '>'],
            mediumLst: Array.isArray(threshold.mediumLst) ? threshold.mediumLst : [threshold.mediumLst || 45, '<'],
            lowNdvi: Array.isArray(threshold.lowNdvi) ? threshold.lowNdvi : [threshold.lowNdvi || 0.5, '>'],
            lowLst: Array.isArray(threshold.lowLst) ? threshold.lowLst : [threshold.lowLst || 40, '<']
          };
        } else if (Array.isArray(threshold) && threshold.length >= 6) {
          // Legacy format: [highNdvi, highLst, mediumNdvi, mediumLst, lowNdvi, lowLst]
          thresholds = {
            highNdvi: Array.isArray(threshold[0]) ? threshold[0] : [threshold[0] || 0.1, '<'],
            highLst: Array.isArray(threshold[1]) ? threshold[1] : [threshold[1] || 45, '>'],
            mediumNdvi: Array.isArray(threshold[2]) ? threshold[2] : [threshold[2] || 0.1, '>'],
            mediumLst: Array.isArray(threshold[3]) ? threshold[3] : [threshold[3] || 45, '<'],
            lowNdvi: Array.isArray(threshold[4]) ? threshold[4] : [threshold[4] || 0.5, '>'],
            lowLst: Array.isArray(threshold[5]) ? threshold[5] : [threshold[5] || 40, '<']
          };
        } else {
          // Default thresholds
          thresholds = {
            highNdvi: [0.1, '<'],
            highLst: [45, '>'],
            mediumNdvi: [0.1, '>'],
            mediumLst: [45, '<'],
            lowNdvi: [0.5, '>'],
            lowLst: [40, '<']
          };
        }
        
        severityLevel = calculateSurfaceTemperatureSeverityLevel(
          record.ndvi,
          record.lst,
          thresholds.highNdvi,
          thresholds.highLst,
          thresholds.mediumNdvi,
          thresholds.mediumLst,
          thresholds.lowNdvi,
          thresholds.lowLst
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].vegetation_index = record.vegetation_index;
      wardData[wardNum].surface_temp_c = record.surface_temp_c;
      wardData[wardNum].land_use_type = record.land_use_type;
      wardData[wardNum].ndvi = record.ndvi;
      wardData[wardNum].lst = record.lst;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchSurfaceTemperatureTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/surface_temperature`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], vegetation_index: [], surface_temp_c: [], land_use_type: [], ndvi: [], lst: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].vegetation_index.push(Number(record.vegetation_index));
      byDate[d].surface_temp_c.push(Number(record.surface_temp_c));
      byDate[d].land_use_type.push(record.land_use_type);
      byDate[d].ndvi.push(Number(record.ndvi));
      byDate[d].lst.push(Number(record.lst));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        vegetation_index: records.map(r => Number(r.vegetation_index)).filter(v => !isNaN(v)),
        surface_temp_c: records.map(r => Number(r.surface_temp_c)).filter(v => !isNaN(v)),
        land_use_type: records.map(r => r.land_use_type).filter(Boolean),
        ndvi: records.map(r => Number(r.ndvi)).filter(v => !isNaN(v)),
        lst: records.map(r => Number(r.lst)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      vegetation_index: dates.map(d => {
        const arr = (byDate[d]?.vegetation_index || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      surface_temp_c: dates.map(d => {
        const arr = (byDate[d]?.surface_temp_c || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      land_use_type: dates.map(d => (byDate[d]?.land_use_type?.[0] ?? null)),
      ndvi: dates.map(d => {
        const arr = (byDate[d]?.ndvi || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      lst: dates.map(d => {
        const arr = (byDate[d]?.lst || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchSurfaceTemperatureSeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/surface_temperature`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

// ─── Shelter Occupancy API ───────────────────────────────────────────────────────
// Data for "Homeless and street dwellers (Shelter Occupancy)" indicator
// API endpoint: /api/dashboard/severity/shelter_occupancy
// Returns: severity_index, occupancy_rate

export const fetchShelterOccupancyDataForWards = async (startDate = '2025-02-03', endDate = '2025-02-09', threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/shelter_occupancy`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    console.log(`[API] 🌐 START: ${endpoint} | Date Range: ${startDate} to ${endDate} | Threshold: ${threshold || 'default'}${city ? ` | City: ${city}` : ''}`);

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} | No records | Duration: ${duration}ms`);
      return {};
    }

    const wardData = {};
    records.forEach(record => {
      const wardNum = record.ward_number;
      if (!wardNum) return;

      if (!wardData[wardNum]) {
        wardData[wardNum] = {
          ward_number: wardNum,
          ward_name: record.ward_name || `Ward ${wardNum}`,
          severity_index: record.severity_index,
          occupancy_rate: record.occupancy_rate,
          severity_counts: { LOW: 0, MEDIUM: 0, HIGH: 0 },
          record_count: 0
        };
      }

      // Recalculate severity if occupancy_rate is available and threshold is provided
      let severityLevel = record.severity_level;
      if (threshold !== null && record.occupancy_rate !== undefined && record.occupancy_rate !== null) {
        // Handle threshold format: [[value, operator], [value, operator], [value, operator]]
        let thresholds = threshold;
        if (!Array.isArray(threshold)) {
          thresholds = [[threshold, '>'], [threshold, '<'], [threshold * 0.25, '<']];
        } else if (threshold.length > 0 && !Array.isArray(threshold[0])) {
          // Legacy format: [value, value, value]
          thresholds = [[threshold[0] || 40, '>'], [threshold[1] || 40, '<'], [threshold[2] || 10, '<']];
        }
        severityLevel = calculateShelterOccupancySeverityLevel(
          record.occupancy_rate,
          thresholds[0] || [40, '>'],
          thresholds[1] || [40, '<'],
          thresholds[2] || [10, '<']
        );
      }

      const normalizedSeverity = String(severityLevel || 'LOW').toUpperCase();
      wardData[wardNum].severity_counts[normalizedSeverity] = 
        (wardData[wardNum].severity_counts[normalizedSeverity] || 0) + 1;
      wardData[wardNum].severity_index = record.severity_index;
      wardData[wardNum].occupancy_rate = record.occupancy_rate;
      wardData[wardNum].record_count += 1;
    });

    Object.keys(wardData).forEach(ward => {
      const counts = wardData[ward].severity_counts;
      wardData[ward].dominant_severity = 
        counts.HIGH > 0 ? 'HIGH' :
        counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
    });

    console.log(`[API] ✅ SUCCESS: ${endpoint} | Duration: ${duration}ms | Wards: ${Object.keys(wardData).length}`);
    return wardData;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[API] 🔴 NETWORK ERROR: ${endpoint} | Duration: ${duration}ms`);
      throw new Error(`Network error: Cannot reach API at ${API_BASE_URL}. Is the server running?`);
    }
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchShelterOccupancyTrendData = async (startDate = '2025-02-03', endDate = '2025-02-09', wardNumber = null, threshold = null, city = null) => {
  const endpoint = `/api/dashboard/severity/shelter_occupancy`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (trend) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    let records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
    if (wardNumber) {
      records = records.filter(r => r.ward_number == wardNumber);
    }
    if (!records.length) {
      console.warn(`[API] ⚠️ WARNING: ${endpoint} (trend) | No records | Duration: ${duration}ms`);
      return null;
    }

    const toDateKey = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const dt = new Date(val);
      if (isNaN(dt.getTime())) return null;
      const y = dt.getFullYear(), m = String(dt.getMonth() + 1).padStart(2, '0'), day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const byDate = {};
    records.forEach(record => {
      const d = toDateKey(record.date || record.measurement_date || record.outage_date || record.record_date);
      if (!d) return;
      if (!byDate[d]) {
        byDate[d] = { dates: [], severity_index: [], occupancy_rate: [] };
      }
      byDate[d].dates.push(d);
      byDate[d].severity_index.push(Number(record.severity_index));
      byDate[d].occupancy_rate.push(Number(record.occupancy_rate));
    });

    let dates = Object.keys(byDate).sort();
    if (dates.length === 0 && records.length > 0) {
      const rangeStart = (typeof startDate === 'string' ? startDate : startDate?.toISOString?.()?.slice(0, 10) || '2025-02-01').substring(0, 10);
      dates = [rangeStart];
      byDate[rangeStart] = {
        dates: [rangeStart],
        severity_index: records.map(r => Number(r.severity_index)).filter(v => !isNaN(v)),
        occupancy_rate: records.map(r => Number(r.occupancy_rate)).filter(v => !isNaN(v))
      };
    }
    const trendData = {
      dates,
      severity_index: dates.map(d => {
        const arr = (byDate[d]?.severity_index || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      }),
      occupancy_rate: dates.map(d => {
        const arr = (byDate[d]?.occupancy_rate || []).filter(v => !isNaN(v));
        return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      })
    };

    console.log(`[API] ✅ SUCCESS: ${endpoint} (trend) | Duration: ${duration}ms | Points: ${dates.length}`);
    return trendData;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 EXCEPTION: ${endpoint} (trend) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

export const fetchShelterOccupancySeverityDataForDateRange = async (startDate, endDate, city = null) => {
  const endpoint = `/api/dashboard/severity/shelter_occupancy`;
  const startTime = Date.now();

  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      include_weather: 'true'
    });
    const fullUrl = `${url}?${params}`;

    const response = await fetch(fullUrl, { headers: getApiHeaders(city) });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[API] ❌ FAILED: ${endpoint} (severity) | Status: ${response.status} | Duration: ${duration}ms`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[API] ✅ SUCCESS: ${endpoint} (severity) | Duration: ${duration}ms | Records: ${(result.ward_data || result.data || []).length}`);
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[API] 🔴 ERROR: ${endpoint} (severity) | Error: ${error.message} | Duration: ${duration}ms`);
    throw error;
  }
};

